import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table2, ShieldAlert, User as UserIcon, Home } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigation } from '../../../hooks/useNavigation';
import { BreadcrumbSubpageDropdown } from './BreadcrumbSubpageDropdown';
import { ProjectSwitcherDropdown } from './ProjectSwitcherDropdown';
import { DatastoreSwitcherDropdown } from './DatastoreSwitcherDropdown';

interface WorkspaceBreadcrumbsProps {
  onOpenCreateOrg: () => void;
  onOpenCreateProj: () => void;
}

export const WorkspaceBreadcrumbs: React.FC<WorkspaceBreadcrumbsProps> = ({
  onOpenCreateOrg,
  onOpenCreateProj,
}) => {
  const location = useLocation();
  const {
    user,
    currentOrg,
    projects,
    currentProject,
    setCurrentProject,
    tables,
    currentTable,
    setCurrentTable,
    role,
  } = useAuth();

  const canManageOrg = Boolean(user?.is_superadmin || role === 'owner' || role === 'admin');

  const {
    orgId,
    projId,
    isHomeMode,
    isAdminMode,
    isAccountMode,
    isProjectScope,
    isTableScope,
    orgNavItems,
    projectNavItems,
    tableNavItems,
    accountNavItems,
    adminNavItems,
    activeOrgItem,
    activeProjectItem,
    activeTableItem,
    activeAccountItem,
    activeAdminItem,
  } = useNavigation();

  const projMatch = location.pathname.match(/\/projects\/([^/]+)/);
  const currentProjIdFromUrl = projMatch ? projMatch[1] : null;
  const activeProject =
    (currentProjIdFromUrl ? projects.find((p) => p.id === currentProjIdFromUrl) : null) ||
    currentProject;

  const tableMatch = location.pathname.match(/\/tables\/([^/]+)/);
  const currentTableIdFromUrl = tableMatch ? tableMatch[1] : null;
  const activeTable =
    (currentTableIdFromUrl ? tables.find((t) => t.id === currentTableIdFromUrl) : null) ||
    currentTable;

  return (
    <div className="flex items-center gap-1 sm:gap-1.5 text-xs text-cf-textSecondary min-w-0 flex-1 overflow-x-auto no-scrollbar whitespace-nowrap">
      {isAdminMode ? (
        /* PLATFORM ADMIN BREADCRUMB */
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-rose-500/10 border border-rose-500/30 text-xs font-medium text-rose-600 dark:text-rose-400 shrink-0">
            <ShieldAlert size={12} className="text-rose-500 flex-shrink-0" />
            <span>Admin</span>
          </div>
          <span className="text-cf-textMuted font-normal">/</span>
          <BreadcrumbSubpageDropdown
            activeItem={activeAdminItem}
            items={adminNavItems}
            title="Views"
          />
        </div>
      ) : isAccountMode ? (
        /* ACCOUNT BREADCRUMB */
        <div className="flex items-center gap-1.5 shrink-0">
          <Link
            to="/account"
            className="flex items-center gap-1 px-1.5 py-1 rounded hover:bg-cf-surfaceActive font-medium text-cf-textPrimary transition-colors shrink-0"
          >
            <UserIcon size={13} className="text-cf-orange flex-shrink-0" />
            <span className="max-w-[85px] sm:max-w-none truncate">Account</span>
          </Link>
          <span className="text-cf-textMuted font-normal">/</span>
          <BreadcrumbSubpageDropdown
            activeItem={activeAccountItem}
            items={accountNavItems}
            title="Views"
          />
        </div>
      ) : isHomeMode ? (
        /* HOME BREADCRUMB */
        <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-cf-surfaceActive/60 text-cf-textPrimary font-semibold text-xs shrink-0">
          <Home size={13} className="text-cf-orange flex-shrink-0" />
          <span>Home</span>
        </div>
      ) : (
        /* WORKSPACE BREADCRUMB CHAIN (Project / Table / Views) */
        <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
          {!isProjectScope ? (
            /* Organization Views Dropdown */
            <BreadcrumbSubpageDropdown
              activeItem={activeOrgItem}
              items={orgNavItems}
              title="Views"
            />
          ) : (
            /* Project & Table Hierarchy */
            <>
              {/* Selected Project Dropdown */}
              <ProjectSwitcherDropdown
                projects={projects}
                activeProject={activeProject}
                currentOrgId={currentOrg?.id}
                canManageOrg={canManageOrg}
                onSelectProject={(p) => setCurrentProject(p)}
                onOpenCreateProject={onOpenCreateProj}
              />

              <span className="text-cf-textMuted font-normal">/</span>

              {/* Datastores => Selected Datastore Chain */}
              {isTableScope ? (
                <>
                  {/* "Datastores" link back to project tables */}
                  <Link
                    to={
                      orgId && projId
                        ? `/organization/${orgId}/projects/${projId}/datastores`
                        : projId
                        ? `/projects/${projId}/datastores`
                        : '/'
                    }
                    className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-cf-surfaceActive font-medium text-cf-textSecondary hover:text-cf-textPrimary transition-colors shrink-0"
                    title="Back to Datastores"
                  >
                    <Table2 size={13} className="text-cf-orange flex-shrink-0" />
                    <span>Datastores</span>
                  </Link>

                  <span className="text-cf-textMuted font-normal">/</span>

                  {/* Selected Datastore Switcher */}
                  <DatastoreSwitcherDropdown
                    tables={tables}
                    activeTable={activeTable}
                    orgId={orgId}
                    projId={projId || currentProject?.id}
                    onSelectTable={(t) => setCurrentTable(t)}
                  />

                  {/* Current Table Subpage View */}
                  {activeTableItem && (
                    <>
                      <span className="text-cf-textMuted font-normal">/</span>
                      <BreadcrumbSubpageDropdown
                        activeItem={activeTableItem}
                        items={tableNavItems}
                        title="Views"
                      />
                    </>
                  )}
                </>
              ) : (
                /* Project Subpage Views Dropdown */
                <BreadcrumbSubpageDropdown
                  activeItem={activeProjectItem}
                  items={projectNavItems}
                  title="Views"
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
